// Iter61r — FrameioReviewSection : Frame.io workflow reusable component.
// Placed inside any task detail (works on ALL tasks, not just a specific category).
//
// Workflow:
//   1. Assignee (monteur) pastes a Frame.io link → task is "submitted", founders get a push notif.
//   2. Founder clicks one of 4 buttons + optional message :
//        🔴 À refaire  |  🟠 Avec correctifs  |  🟢 Quelques retours  |  ✅ Valider
//   3. Monteur sees which type of retour + can resubmit a new link (cycle stored in history).
//   4. Monteur can edit a link he pasted by mistake (✏️ button on each history entry).
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatRelative } from '../constants/users';
import { ExternalLink, Send, Pencil, Check, AlertTriangle } from './icons';


const REVIEW_META = {
  redo:       { label: 'En correction',        emoji: '🔴', color: '#FF6B6B' },
  with_fixes: { label: 'Avec correctifs',  emoji: '🟠', color: '#F5A623' },
  minor:      { label: 'Quelques retours', emoji: '🟢', color: '#34D399' },
  approved:   { label: 'Validée',          emoji: '✅', color: '#5BBEFC' },
};


// Small toggle used at the top of the task edit modal (founder only, gates the Frame.io section).
export function FrameioToggle({ enabled, onToggle }) {
  return (
    <div
      onClick={() => onToggle?.(!enabled)}
      data-testid="frameio-toggle"
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 10,
        background: enabled ? 'rgba(46,163,242,0.14)' : 'var(--sf2)',
        border: `1.5px solid ${enabled ? 'rgba(46,163,242,0.40)' : 'var(--bd)'}`,
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: 6,
        background: enabled ? '#5BBEFC' : 'transparent',
        border: `2px solid ${enabled ? '#5BBEFC' : 'var(--bd)'}`,
        display: 'grid', placeItems: 'center', flexShrink: 0,
        color: '#fff', fontSize: 12, fontWeight: 800,
      }}>{enabled ? '✓' : ''}</div>
      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)' }}>
        🎬 Frame.io {enabled ? 'activé' : 'désactivé'}
      </div>
    </div>
  );
}


export function FrameioReviewSection({ task, users = [], onUpdated }) {
  const { user } = useAuth();
  const [showReviewButtons, setShowReviewButtons] = useState(false);
  // Iter61t — null-safe render: caller may pass task=null during transitions
  if (!task) return null;
  const isFounder = user?.role === 'founder';
  const isAssignee = (
    task.assigned_to === user?.id
    || task.assigned_to_montage === user?.id
    || (task.assignees || []).includes(user?.id)
  );
  const history = task.frameio_history || [];
  const lastEntry = history[history.length - 1] || null;
  const state = task.frameio_state || null; // null | 'submitted' | 'reviewed' | 'approved'

  return (
    <div
      data-testid="frameio-section"
      style={{
        padding: 14, borderRadius: 12,
        background: 'rgba(46,163,242,0.06)',
        border: '1.5px solid rgba(46,163,242,0.28)',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>🎬</span>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: 'var(--t1)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Frame.io — Validation vidéo
        </div>
        {state && (
          <div style={{
            marginLeft: 'auto', padding: '3px 9px', borderRadius: 999,
            background: state === 'approved' ? 'rgba(52,211,153,0.16)' : state === 'submitted' ? 'rgba(46,163,242,0.16)' : 'rgba(245,166,35,0.16)',
            border: `1px solid ${state === 'approved' ? 'rgba(52,211,153,0.35)' : state === 'submitted' ? 'rgba(46,163,242,0.35)' : 'rgba(245,166,35,0.35)'}`,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 10,
            color: state === 'approved' ? '#34D399' : state === 'submitted' ? '#5BBEFC' : '#F5A623',
          }}>
            {state === 'submitted' ? 'En vérification' : state === 'reviewed' ? 'À corriger' : 'Validée'}
          </div>
        )}
      </div>

      {/* History timeline (compact) */}
      {history.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {history.map((h, i) => (
            <HistoryRow
              key={h.id || i}
              entry={h}
              users={users}
              onEditLink={async (newLink) => {
                try {
                  const r = await api.patch(`/tasks/${task.id}/frameio-history/${h.id}`, { frameio_link: newLink });
                  toast.success('Lien mis à jour');
                  onUpdated?.(r.data);
                } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
              }}
              canEdit={isFounder || isAssignee}
              isLast={i === history.length - 1}
            />
          ))}
        </div>
      )}

      {/* Iter61r ter — Only the MONTEUR (assignee) can submit a Frame.io link.
          Founders supervise but can't submit themselves. */}
      {isAssignee && !isFounder && state !== 'submitted' && state !== 'approved' && (
        <SubmitLink task={task} onSubmitted={onUpdated} />
      )}

      {/* Iter61r ter — When submitted and reviewed, founder sees "waiting for new link" */}
      {isFounder && state === 'reviewed' && (
        <div style={{
          padding: '10px 12px', borderRadius: 9,
          background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)',
          fontFamily: 'Lexend', fontSize: 12, color: '#F5A623', textAlign: 'center',
        }}>
          ⏳ En attente du nouveau lien et de la nouvelle note du monteur…
        </div>
      )}

      {/* Founder review — gated by "Étape suivante" button per user UX request */}
      {isFounder && state === 'submitted' && lastEntry && !showReviewButtons && (
        <button
          data-testid="frameio-next-step"
          onClick={() => setShowReviewButtons(true)}
          style={{
            padding: '11px 14px', borderRadius: 10,
            background: 'linear-gradient(135deg, #2EA3F2 0%, #5BBEFC 100%)',
            border: 'none', color: '#fff',
            cursor: 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          Étape suivante →
        </button>
      )}
      {isFounder && state === 'submitted' && lastEntry && showReviewButtons && (
        <ReviewButtons
          task={task}
          onReviewed={(u) => { setShowReviewButtons(false); onUpdated?.(u); }}
          onCancel={() => setShowReviewButtons(false)}
        />
      )}

      {/* Iter61r ter — allow the founder to CHANGE their review after clicking a button
          (in case they picked the wrong one). Shows on 'reviewed' or 'approved' states. */}
      {isFounder && (state === 'reviewed' || state === 'approved') && lastEntry && !showReviewButtons && (
        <button
          data-testid="frameio-change-review"
          onClick={() => setShowReviewButtons(true)}
          style={{
            padding: '9px 12px', borderRadius: 9,
            background: 'transparent',
            border: '1.5px solid var(--bd)',
            color: 'var(--t2)',
            cursor: 'pointer',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          ↺ Modifier ma décision
        </button>
      )}
      {isFounder && (state === 'reviewed' || state === 'approved') && lastEntry && showReviewButtons && (
        <ReviewButtons
          task={task}
          onReviewed={(u) => { setShowReviewButtons(false); onUpdated?.(u); }}
          onCancel={() => setShowReviewButtons(false)}
        />
      )}

      {/* Empty state */}
      {history.length === 0 && !isAssignee && !isFounder && (
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', fontStyle: 'italic' }}>
          Aucun lien Frame.io soumis.
        </div>
      )}
    </div>
  );
}


function HistoryRow({ entry, users, onEditLink, canEdit, isLast }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.frameio_link || '');
  const submitter = users.find((u) => u.id === entry.submitted_by);
  const reviewer = users.find((u) => u.id === entry.reviewed_by);
  const meta = entry.review_status ? REVIEW_META[entry.review_status] : null;

  return (
    <div
      data-testid={`frameio-entry-${entry.id}`}
      style={{
        padding: '9px 11px', borderRadius: 9,
        background: isLast ? 'var(--sf2)' : 'transparent',
        border: `1px solid ${isLast ? 'var(--bd)' : 'transparent'}`,
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      {editing ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="https://frame.io/reviews/..."
            style={{ flex: 1, padding: '7px 10px', borderRadius: 7, background: 'var(--sf)', border: '1px solid var(--bd)', fontFamily: 'Lexend', fontSize: 12, color: 'var(--t1)' }}
            autoFocus
          />
          <button
            data-testid="frameio-edit-save"
            onClick={async () => { if (!draft.trim().startsWith('http')) return toast.error('Lien invalide'); await onEditLink(draft.trim()); setEditing(false); }}
            style={{ padding: '6px 10px', borderRadius: 7, background: '#5BBEFC', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'Sora', fontWeight: 700, fontSize: 11 }}
          >OK</button>
          <button onClick={() => { setDraft(entry.frameio_link || ''); setEditing(false); }} style={{ padding: '6px 10px', borderRadius: 7, background: 'transparent', color: 'var(--t3)', border: '1px solid var(--bd)', cursor: 'pointer', fontSize: 11 }}>✕</button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a
            href={entry.frameio_link}
            target="_blank" rel="noopener noreferrer"
            data-testid={`frameio-open-${entry.id}`}
            style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: '#5BBEFC', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            <ExternalLink size={12} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.frameio_link}</span>
          </a>
          {canEdit && (
            <button
              data-testid={`frameio-edit-btn-${entry.id}`}
              onClick={() => setEditing(true)}
              title="Corriger le lien"
              style={{ padding: 4, borderRadius: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--t3)' }}
            >
              <Pencil size={11} />
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)' }}>
        {submitter && <span>{submitter.first_name || submitter.name}</span>}
        <span>·</span>
        <span>{formatRelative(entry.submitted_at)}</span>
        {entry.notes && (
          <>
            <span>·</span>
            <span style={{ color: 'var(--t2)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>« {entry.notes} »</span>
          </>
        )}
      </div>

      {meta && (
        <div style={{
          padding: '6px 9px', borderRadius: 8,
          background: `${meta.color}18`, border: `1px solid ${meta.color}55`,
          display: 'flex', flexDirection: 'column', gap: 3,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>{meta.emoji}</span>
            <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11, color: meta.color }}>{meta.label}</span>
            {reviewer && <span style={{ fontFamily: 'Lexend', fontSize: 10, color: 'var(--t3)', marginLeft: 'auto' }}>{reviewer.first_name || reviewer.name} · {formatRelative(entry.reviewed_at)}</span>}
          </div>
          {entry.review_notes && (
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t1)', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
              {entry.review_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function SubmitLink({ task, onSubmitted }) {
  const [link, setLink] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const url = link.trim();
    if (!url.startsWith('http')) return toast.error('Lien Frame.io invalide');
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${task.id}/frameio-submit`, { frameio_link: url, notes: notes.trim() || null });
      toast.success('Frame.io soumis pour validation');
      setLink(''); setNotes('');
      onSubmitted?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <input
        data-testid="frameio-submit-input"
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="Colle le lien Frame.io"
        style={{
          padding: '9px 12px', borderRadius: 8,
          background: 'var(--sf2)', border: '1px solid var(--bd)',
          fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)', outline: 'none',
        }}
      />
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Note pour le founder (optionnel)"
        style={{
          padding: '8px 12px', borderRadius: 8,
          background: 'var(--sf2)', border: '1px solid var(--bd)',
          fontFamily: 'Lexend', fontSize: 12, color: 'var(--t2)', outline: 'none',
        }}
      />
      <button
        data-testid="frameio-submit-btn"
        onClick={submit}
        disabled={!link.trim() || busy}
        style={{
          padding: '9px 12px', borderRadius: 8,
          background: link.trim() && !busy ? '#5BBEFC' : 'var(--sf2)',
          color: '#fff', border: 'none', cursor: link.trim() && !busy ? 'pointer' : 'not-allowed',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}
      >
        <Send size={12} /> {busy ? 'Envoi…' : 'Soumettre pour validation'}
      </button>
    </div>
  );
}


function ReviewButtons({ task, onReviewed, onCancel }) {
  // Iter61r ter — 2-step flow : (1) pick a status + type notes  (2) click "Envoyer notif"
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const confirmAndSend = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${task.id}/frameio-review`, {
        review_status: selected,
        review_notes: notes.trim() || null,
      });
      toast.success(`${REVIEW_META[selected].emoji} ${REVIEW_META[selected].label} — notif envoyée`);
      setSelected(null); setNotes('');
      onReviewed?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t2)' }}>
          1. Message (optionnel) · 2. Choisis un retour · 3. Envoie la notif
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            style={{ padding: '4px 10px', borderRadius: 7, background: 'transparent', border: '1px solid var(--bd)', color: 'var(--t3)', fontSize: 11, cursor: 'pointer', fontFamily: 'Sora' }}
          >← Retour</button>
        )}
      </div>
      <textarea
        data-testid="frameio-review-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Message pour le monteur (optionnel — s'applique au choix ci-dessous)"
        rows={2}
        style={{
          padding: '8px 12px', borderRadius: 8,
          background: 'var(--sf2)', border: '1px solid var(--bd)',
          fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)', outline: 'none', resize: 'vertical',
        }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
        {(['redo', 'with_fixes', 'minor', 'approved']).map((s) => {
          const m = REVIEW_META[s];
          const isSelected = selected === s;
          return (
            <button
              key={s}
              data-testid={`frameio-review-${s}`}
              onClick={() => setSelected(s)}
              disabled={busy}
              style={{
                padding: '10px 8px', borderRadius: 9,
                background: isSelected ? m.color : `${m.color}20`,
                border: `1.5px solid ${isSelected ? m.color : `${m.color}66`}`,
                color: isSelected ? '#fff' : m.color,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5,
                cursor: busy ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                transition: 'transform 0.15s ease, background 0.15s ease',
              }}
            >
              <span>{m.emoji}</span> {m.label}
            </button>
          );
        })}
      </div>
      {selected && (
        <button
          data-testid="frameio-send-notif"
          onClick={confirmAndSend}
          disabled={busy}
          style={{
            padding: '11px 14px', borderRadius: 10,
            background: busy ? 'var(--sf2)' : 'linear-gradient(135deg, #34D399 0%, #5BBEFC 100%)',
            border: 'none', color: '#fff',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Envoi…' : `📨 Envoyer notif — ${REVIEW_META[selected].emoji} ${REVIEW_META[selected].label}`}
        </button>
      )}
    </div>
  );
}
